import styles from './QuestionNoAuth.module.scss';
import { Text } from '../../../components/typography';
import { useMemo, useState, useCallback } from 'react';
import { Button } from '../../../components';
import {
  inputTypes,
  dateOptions,
  numberCheck,
  statusType,
} from '../../../utilities/inspection';
import { Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import MultiSelect from '../../../components/forms/MultiSelect';
import { LocalizationProvider } from '@mui/x-date-pickers';
import {
  DateTimePicker,
  MobileDateTimePicker,
  TimePicker,
  CalendarIcon,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useIsMobile } from '../../../hooks/misc';
import { InputAdornment } from '@mui/material';
import classNames from 'classnames';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import Icon from '../../../components/Icon';
import { sanitizeArray } from '../../../utilities';
import moment from 'moment';
import FailureModalNoAuth from './FailureModalNoAuth';
import RichTextEditor from '../../../components/rich_text/RichTextEditor';
import ImageWrapper from '../../../components/offline-wrapper-functions/image-wrapper';
import SignatureInputNoAuth from './SignatureNoAuth';
import RiskMatrixNoAuth from './RiskMatrixNoAuth';
import FileUploadInput from '../../../components/files/FileUploadInput';
import { handleFileUpload } from './utils';
import { useApolloClient } from '@apollo/client';
import findFileIcon from '../../../utilities/files';
import { getExtension } from '../../../components/files/utils';
import Select from 'react-select';
import { customColors } from '../../../utilities/misc';

const submitAnswerMutation = loader('./QuestionNoAuth.answer.graphql');
const updateAnswerMutation = loader('./QuestionNoAuth.updateAnswer.graphql');

export default function QuestionNoAuth({
  question = {},
  index,
  inspectionId,
  setAssessmentState,
  onLocalAnswer,
  company = null,
}) {
  const client = useApolloClient();
  const {
    title = '',
    description = '',
    required = false,
    additionalData = null,
    inputType = '',
  } = question;

  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [submitAnswer] = useMutation(submitAnswerMutation, {
    onCompleted: (data) => {
      const result = data?.addInspectionItem;
      setAnswer((prev) => ({ ...prev, id: result?.id }));
      setAssessmentState?.((prev) => ({
        ...prev,
        items: [...(prev?.items ?? []), { ...result, ...answer }],
      }));
      setLoading(false);
    },
  });
  const [updateAnswer] = useMutation(updateAnswerMutation, {
    onCompleted: () => setLoading(false),
  });
  const [state, setState] = useState(question);
  const [isEditing, setIsEditing] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [numberPass, setNumberPass] = useState(true);
  const [failureModalState, setFailureModalState] = useState({
    show: false,
    title: '',
    description: '',
  });

  const openFailureModal = ({ description, title }) =>
    setFailureModalState({ show: true, title, description });

  const handleChange = useCallback(
    (options) => {
      if (!inspectionId) {
        // Local-only mode: accumulate answers in parent without persisting to server
        setLoading(false);
        setAnswer((prev) => ({
          ...prev,
          ...options,
          id: `local-${state?.id}`,
        }));
        setState((prev) => ({
          ...prev,
          ...options,
          id: state?.id,
          questionId: state?.id,
        }));
        onLocalAnswer?.(state?.id, { ...options });
        if (options?.failed) {
          openFailureModal({ ...state });
        }
        setIsEditing(false);
        return;
      }

      let handler = null;
      const answerId = answer?.id;

      if (!answerId) {
        handler = submitAnswer;
      } else {
        handler = updateAnswer;
      }

      handler({
        variables: {
          ...state,
          ...options,
          [answerId ? 'id' : 'questionId']: answerId ?? state?.id,
          inspectionId,
        },
      }).then(({ data } = {}) => {
        const key = answerId ? 'updateInspectionItem' : 'addInspectionItem';
        const id = data?.[key]?.id;
        if (id) setAnswer({ ...options, id });
      });
      setState((prev) => ({
        ...prev,
        ...options,
        id: state?.id,
        questionId: state?.id,
        inspectionId,
      }));
      if (options?.failed) {
        openFailureModal({ ...state });
      }
      setIsEditing(false);
    },
    [
      submitAnswer,
      updateAnswer,
      state,
      answer?.id,
      inspectionId,
      onLocalAnswer,
    ],
  );

  const questionInput = useMemo(() => {
    const { additionalData, inputType } = question;
    const dateFormat = dateOptions[inputType];

    const DateTimePickerComponent =
      inputType === inputTypes.time
        ? TimePicker
        : isMobile && inputType === inputTypes.dateTime
        ? MobileDateTimePicker
        : DateTimePicker;

    const pickerStyles = isMobile
      ? {
          textField: {
            sx: { minWidth: '100%' },
            InputProps: {
              endAdornment: (
                <InputAdornment
                  sx={{
                    color: '#979797',
                  }}
                  position="end"
                >
                  <CalendarIcon />
                </InputAdornment>
              ),
            },
          },
        }
      : {
          textField: {
            sx: {
              minWidth: '50%',
              marginTop: '0.5rem',
              backgroundColor: 'white',
            },
          },
        };
    switch (question?.inputType) {
      case inputTypes.test:
        const buttonText = additionalData
          ? additionalData?.split('|')
          : ['Pass', 'Fail'];
        return (
          (isEditing || !answer?.id) && (
            <div className={styles.flexRowGapSm}>
              <Button
                variant="success"
                size="sm"
                value={buttonText[0]}
                onClick={() =>
                  handleChange({
                    value: statusType.pass,
                    status: statusType.pass,
                    failed: false,
                  })
                }
              />
              <Button
                variant="danger"
                size="sm"
                value={buttonText[1]}
                onClick={() =>
                  handleChange({
                    value: statusType.fail,
                    status: statusType.fail,
                    failed: true,
                  })
                }
              />
              {buttonText?.length > 2 && (
                <Button
                  variant="secondary"
                  size="sm"
                  value={buttonText[2]}
                  onClick={() =>
                    handleChange({
                      value: statusType.notApplicable,
                      status: statusType.notApplicable,
                      failed: false,
                    })
                  }
                />
              )}
            </div>
          )
        );
      case inputTypes.selectChecklist:
      case inputTypes.multiSelect:
        const multiOptions = additionalData?.split('|');
        const isChecklist = question?.inputType === inputTypes.selectChecklist;
        return (
          <MultiSelect
            vertical={isChecklist}
            title={''}
            options={multiOptions}
            values={answer?.value?.split('|') || []}
            onChange={(newValue) => {
              const value = sanitizeArray(newValue);
              handleChange({ value, status: statusType.pass, failed: false });
            }}
          />
        );
      case inputTypes.select:
        const singleOptions = additionalData?.split('|') ?? [];
        return singleOptions?.map((o, index) => (
          <Form.Check
            key={inputType + title + index + description}
            inline
            label={o}
            type={'radio'}
            checked={answer?.value === o}
            value={o}
            onChange={(e) =>
              handleChange({
                value: e.target.value,
                status: statusType.pass,
                failed: false,
              })
            }
          />
        ));
      case inputTypes.number:
        return isEditing || !answer?.id ? (
          <div className={styles.inputContainer}>
            <Form.Control
              type="number"
              onSubmit={(e) => e.preventDefault()}
              isValid={numberPass === true && answer?.value}
              isInvalid={numberPass === false && answer?.value}
              onChange={(e) => {
                const limits = additionalData?.split('|') || [null, null];
                const min = limits[0] !== '' ? parseFloat(limits[0]) : null;
                const max = limits[1] !== '' ? parseFloat(limits[1]) : null;
                const value = parseFloat(e.target.value);
                let pass = true;
                if (min !== null && !isNaN(min) && value < min) {
                  pass = false;
                }
                if (max !== null && !isNaN(max) && value > max) {
                  pass = false;
                }
                setNumberPass(pass);
                setAnswer((prev) => ({ ...prev, value: e.target.value }));
              }}
              onBlur={(e) =>
                handleChange({
                  value: e.target.value,
                  status: numberPass ? statusType.pass : statusType.fail,
                  failed: !numberPass,
                })
              }
              placeholder={t(keys.action.ENTER, {
                variable: t(keys.templates.NUMBER),
              })}
              value={answer?.value}
            />
          </div>
        ) : (
          <Text noMargin>{answer?.value}</Text>
        );
      case inputTypes.text:
      case inputTypes.multiText:
        const isMultiLine = inputType === inputTypes.multiText;
        return isEditing || !answer?.id ? (
          <div className={styles.inputContainer}>
            <Form.Control
              as="textarea"
              onChange={(e) =>
                setAnswer((prev) => ({ ...prev, value: e.target.value }))
              }
              onBlur={(e) =>
                handleChange({
                  value: e.target.value,
                  status: statusType.pass,
                  failed: false,
                })
              }
              placeholder={t(keys.action.ENTER, {
                variable: t(keys.templates.TEXT),
              })}
              value={answer?.value}
              rows={isMultiLine ? 5 : 1}
            />
          </div>
        ) : (
          <Text noMargin>{answer?.value}</Text>
        );
      case inputTypes.time:
      case inputTypes.date:
      case inputTypes.dateTime:
        return (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePickerComponent
              inputType={inputType}
              isMobile={isMobile}
              fullWidth
              closeOnSelect={false}
              slotProps={pickerStyles}
              format={dateFormat.format}
              inputFormat={dateFormat.format}
              views={dateFormat.views}
              timeSteps={dateFormat.timeSteps}
              label={t(keys.action.SELECT_VARIABLE, {
                variable: dateFormat.title,
              })}
              value={
                answer?.value ? moment(answer?.value, dateFormat.format) : null
              }
              onChange={(newValue) =>
                setAnswer((prev) => ({
                  ...prev,
                  value: newValue?.format(dateFormat.format),
                }))
              }
              onAccept={() =>
                handleChange({
                  value: answer?.value,
                  status: statusType.pass,
                  failed: false,
                })
              }
            />
          </LocalizationProvider>
        );
      case inputTypes.signature:
        return (
          <div>
            {isEditing || !answer?.value ? (
              <SignatureInputNoAuth
                originalAdditional={additionalData}
                onSubmit={(newValue, failed, status, additional) =>
                  handleChange({ value: newValue, failed, status, additional })
                }
                company={company}
              />
            ) : (
              <ImageWrapper
                image={answer?.value}
                className={styles.displayImage}
              />
            )}
          </div>
        );
      case inputTypes.multiSignature:
        return (
          <div className={styles.multiSignatureDisplay}>
            {answer?.value
              ? answer?.value?.split('|')?.map((sig, i) => (
                  <div key={sig} className={styles.signature}>
                    <Text
                      key={additionalData?.split('|')[i]}
                      color="accentPrimary"
                      className={styles.textResponse}
                    >
                      {additionalData?.split('|')[i]}
                    </Text>
                    <ImageWrapper
                      alt="signature4"
                      className={styles.displayImage}
                      image={sig}
                    />
                  </div>
                ))
              : null}
            <SignatureInputNoAuth
              originalAdditional={additionalData}
              onSubmit={(newSignature, failed, status, additional) => {
                const newValue = answer?.value
                  ? `${answer?.value}|${newSignature}`
                  : newSignature;
                setAnswer((prev) => ({ ...prev, value: newValue }));
                handleChange({ value: newValue, failed, status, additional });
              }}
              company={company}
            />
          </div>
        );
      case inputTypes.matrix:
        return (
          <RiskMatrixNoAuth
            impact={answer?.impact}
            probability={answer?.probability}
            onChange={(impact, probability) => {
              handleChange({
                value: 'MATRIX',
                impact,
                probability,
                failed: false,
                status: statusType.pass,
              });
            }}
            company={company}
          />
        );
      case inputTypes.file:
        return (
          <div>
            {loading && <Spinner className={styles.spinner} />}
            {isEditing || !answer?.value ? (
              <FileUploadInput
                onChange={(e) =>
                  handleFileUpload({
                    file: e.target.files[0],
                    onSubmit: (res) =>
                      handleChange({
                        value: res.url,
                        failed: false,
                        status: 'PASS',
                        additional: res.name,
                      }),
                    client,
                    company,
                    setLoading,
                  })
                }
              />
            ) : (
              <div className={styles.card}>
                <img
                  className={styles.fileIcon}
                  alt={`${answer?.additional} file`}
                  src={findFileIcon(getExtension(answer?.value))}
                />
                <Text noMargin weight="semiBold">
                  {answer?.additional}
                </Text>
              </div>
            )}
          </div>
        );
      case inputTypes.multiFile:
        return (
          <div>
            {loading && <Spinner className={styles.spinner} />}
            <FileUploadInput
              onChange={(e) =>
                handleFileUpload({
                  file: e.target.files[0],
                  onSubmit: (res) =>
                    handleChange({
                      value: answer?.value
                        ? `${answer?.value}|${res.url}`
                        : res.url,
                      failed: false,
                      status: 'PASS',
                      additional: answer?.value
                        ? `${answer?.additional}|${res.name}`
                        : res.name,
                    }),
                  client,
                  company,
                  setLoading,
                })
              }
            />
            {answer?.additional &&
              answer?.additional?.split('|')?.map((f, i) => (
                <div className={styles.card} key={`mul-file-${f}-${i}`}>
                  <img
                    className={styles.fileIcon}
                    alt={`${f} file`}
                    src={findFileIcon(getExtension(f))}
                  />
                  <Text noMargin weight="semiBold">
                    {f}
                  </Text>
                </div>
              ))}
          </div>
        );
      case inputTypes.image:
        return (
          <div>
            {loading && <Spinner className={styles.spinner} />}
            {isEditing || !answer?.value ? (
              <FileUploadInput
                accept={'image/*'}
                onChange={(e) =>
                  handleFileUpload({
                    file: e.target.files[0],
                    onSubmit: (res) =>
                      handleChange({
                        value: res.url,
                        failed: false,
                        status: 'PASS',
                        additional: res.name,
                      }),
                    client,
                    company,
                    setLoading,
                  })
                }
              />
            ) : (
              <ImageWrapper
                image={answer?.value}
                className={styles.displayImage}
              />
            )}
          </div>
        );
      case inputTypes.multiImage:
        return (
          <div>
            {loading && <Spinner className={styles.spinner} />}
            <FileUploadInput
              accept={'image/*'}
              onChange={(e) =>
                handleFileUpload({
                  file: e.target.files[0],
                  onSubmit: (res) =>
                    handleChange({
                      value: answer?.value
                        ? `${answer?.value}|${res.url}`
                        : res.url,
                      failed: false,
                      status: 'PASS',
                      additional: answer?.value
                        ? `${answer?.additional}|${res.name}`
                        : res.name,
                    }),
                  client,
                  company,
                  setLoading,
                })
              }
            />
            {answer?.value &&
              answer?.value
                ?.split('|')
                ?.map((img, i) => (
                  <ImageWrapper
                    key={`${i}-${img}-mul-img`}
                    image={img}
                    className={styles.displayImage}
                  />
                ))}
          </div>
        );
      case inputTypes.dropdownSingle:
      case inputTypes.dropdownMulti: {
        const isSingle = inputType === inputTypes.dropdownSingle;
        const selectOptions =
          additionalData?.split('|')?.map((a) => ({ label: a, value: a })) ??
          [];
        const selected = answer?.value
          ? answer.value.split('|').map((v) => ({ label: v, value: v }))
          : [];
        return (
          <Select
            isMulti={!isSingle}
            closeMenuOnSelect={isSingle}
            options={selectOptions}
            menuPortalTarget={document.body}
            defaultValue={selected}
            onChange={(newValue) => {
              if (!newValue || (Array.isArray(newValue) && !newValue.length)) {
                handleChange({
                  value: '',
                  status: statusType.pass,
                  failed: false,
                });
                return;
              }
              const value = isSingle
                ? newValue.value
                : newValue.map((v) => v.value).join('|');
              handleChange({ value, status: statusType.pass, failed: false });
            }}
          />
        );
      }
      case inputTypes.gps:
        return (
          <div className={styles.flexRowGapSm}>
            <Text color={answer?.value ? 'secondary' : 'primary'} noMargin>
              {answer?.value || t(keys.assessments.CLICK_GPS)}
            </Text>
            <Icon
              style={{
                color: navigator.geolocation
                  ? customColors.BLUE
                  : customColors.GRAY,
                cursor: navigator.geolocation ? 'pointer' : 'not-allowed',
              }}
              onClick={() => {
                if (!navigator.geolocation) return;
                navigator.geolocation.getCurrentPosition(
                  (loc) => {
                    const lat = loc.coords.latitude.toFixed(4);
                    const lon = loc.coords.longitude.toFixed(4);
                    handleChange({
                      value: `lat: ${lat}, long: ${lon}`,
                      status: statusType.pass,
                      failed: false,
                    });
                  },
                  (err) => console.error({ err }),
                );
              }}
            >
              add_location_alt
            </Icon>
          </div>
        );
      default:
        return (
          <>
            Question Type "{question?.inputType}" not available externally,
            contact your administrator for more info
          </>
        );
    }
  }, [
    question,
    t,
    isMobile,
    handleChange,
    isEditing,
    answer,
    numberPass,
    title,
    description,
    company,
    client,
    loading,
  ]);

  return (
    <div
      className={classNames(
        styles.container,
        index % 2 !== 0 ? styles.odd : styles.even,
      )}
    >
      <div className={styles.header}>
        {question?.inputType === 'MESSAGE' ? (
          <RichTextEditor
            readOnly
            value={title}
            className={styles.richTextContainer}
          />
        ) : (
          <div>
            <Text weight="bold" noMargin>
              {title}{' '}
              <span style={{ color: 'red', fontWeight: 'bold' }}>
                {' '}
                {`${required ? '*' : ''}`}
              </span>
            </Text>
            {description && (
              <Text noMargin size="sm">
                {description}
              </Text>
            )}
            {inputType === inputTypes.number && !!additionalData ? (
              <Text
                className={styles.numberText}
                size="sm"
                color="secondary"
                noMargin
              >
                {numberCheck(additionalData)}
              </Text>
            ) : null}
          </div>
        )}
        {!isEditing && answer?.id && (
          <div>
            <Icon
              className={classNames(
                answer?.status === statusType.fail
                  ? styles.red
                  : answer?.status === statusType.pass
                  ? styles.green
                  : styles.gray,
              )}
            >
              {answer?.status === statusType.fail
                ? 'cancel'
                : answer?.status === statusType.pass
                ? 'check_circle'
                : 'check_box'}
            </Icon>
            {inputType !== inputTypes.test && (
              <Icon
                className={styles.red}
                onClick={() => {
                  openFailureModal({
                    title,
                    description,
                  });
                }}
                baseClassName="material-icons"
              >
                flag
              </Icon>
            )}
            <Icon
              className={styles.edit}
              onClick={() => {
                setIsEditing(true);
                setAnswer((prev) => ({ ...prev, value: null }));
              }}
            >
              edit
            </Icon>
          </div>
        )}
      </div>
      {question?.inputType !== 'MESSAGE' && questionInput}
      {!!answer?.failNotes && (
        <Text color="red" noMargin>
          {answer?.failNotes}
        </Text>
      )}
      {failureModalState?.show && (
        <FailureModalNoAuth
          modalState={failureModalState}
          setModalState={setFailureModalState}
          onSubmit={(notes) => {
            handleChange({
              value: answer?.value,
              failNotes: notes,
              failed: true,
              status: statusType.fail,
            });
          }}
        />
      )}
    </div>
  );
}
